import type { ConflictCode } from '../../services/api/types.gen';

/**
 * Copy for marking an evaluation as not needing a physician signature order.
 *
 * The server `detail` is intentionally never surfaced (see
 * `.cursor/rules/error-handling.mdc`); we key off the stable `code`. Keyed by
 * the generated `ConflictCode` union rather than free-form strings so that
 * renaming a code in `app/error_codes.py` and regenerating the client fails the
 * build here, instead of silently falling through to the generic copy this map
 * exists to avoid.
 *
 * Each refusal names the state the order is actually in and the one action that
 * clears it, because a user told only that it failed has no way to find the
 * walk-back control in Orders Manager. The two states need separate wording:
 * telling someone their signed order is "out for signature" would send them to
 * the wrong tab looking for something that is not there.
 */
const COPY_BY_CODE: Partial<Record<ConflictCode, string>> = {
  VISIT_SIGNATURE_REQUEST_SENT:
    'This order has already been sent for signature and cannot be removed from this evaluation. If the order is no longer needed, use "Move to To Be Sent" in Order Manager first, then use "Mark as not an order" here.',
  VISIT_SIGNATURE_REQUEST_SIGNED:
    'This order has already been signed and returned, so it cannot be removed from this evaluation. If the order is no longer needed, use "Move to Pending Signature" and then "Move to To Be Sent" in Order Manager, then use "Mark as not an order" here.',
  CONFLICT:
    'This order can no longer be removed from this evaluation. Check its status in Order Manager.',
};

export const WAIVE_EVAL_ORDER_FALLBACK_COPY =
  'Unable to update the order. Please try again.';

export function waiveEvalOrderRefusalCopy(
  code?: ConflictCode | string | null
): string {
  // Only ever called for a confirmed 409, so an unmapped code is still a
  // refusal and must not invite a retry that will fail the same way.
  return (
    COPY_BY_CODE[code as ConflictCode] ??
    COPY_BY_CODE.CONFLICT ??
    WAIVE_EVAL_ORDER_FALLBACK_COPY
  );
}

/**
 * What the confirmation asks before an evaluation is marked.
 *
 * The menu item is offered whatever the chart's status, and whether an order
 * exists behind it is not something the client can know: it is raised on
 * completion, survives a reopen, and may already have been deleted from Orders
 * Manager, while the visit payload reports only whether this visit type raises
 * one at all. So the wording covers both cases rather than guessing between
 * them. Promising removal outright would describe damage that cannot happen on
 * a chart that was never completed; saying no order will be raised would
 * understate a reopened evaluation, where marking really does delete a live
 * order and its attachments. It closes on the mark being reversible, so the
 * choice is made on what actually happens rather than on how final it sounds.
 */
export const WAIVE_EVAL_ORDER_CONFIRM = {
  title: 'Mark as not an order?',
  message:
    'If this evaluation has raised a signature order and it has not been sent yet, it will be removed along with anything attached to it, and completing the evaluation will not raise another. You can undo this by marking the evaluation as needing an order again.',
  confirmLabel: 'Mark as not an order',
} as const;
